import { Alert, AppBar, Box, Button, CircularProgress, Paper, Tab, Tabs, Typography, useMediaQuery, useTheme } from '@mui/material';
import { ReactNode, useEffect, useRef, useState } from 'react';
import { useApi } from '../../../../services/HttpService';
import { EmailSetting, newEmailSetting, EmailProvider, newEmailProvider } from '../../../../model/Email';
import { EmailSettingForm, EmailSettingFormRef } from '../../../../model/forms/EmailSettingForm';
import { TabPanel, TabProps } from '../../../../components/ui/TabPanel';
import { DataInfoTable, DataListInfoTable } from '../../../../components/tables/DataTable';
import { EmailProviderForm, EmailProviderFormRef } from '../../../../model/forms/EmailProviderForm';

interface EmailSettingOverviewProps {
  item: EmailSetting;
  getError?: string;
  onClose?: () => void;
  EmailProviders?: EmailProvider[];
  onItemChange?: (item: EmailSetting) => void;
}

export const EmailSettingOverview = (props: EmailSettingOverviewProps) => {
  const theme = useTheme();
  const { getError, EmailProviders, onItemChange } = props;
  const { api, error, isLoading } = useApi();
  const { api: modApi, error: modError, isLoading: modIsLoading } = useApi();
  const addEmailSettingRef = useRef<EmailSettingFormRef>(null);
  const [item, setItem] = useState<EmailSetting>(props.item);
  const [displayData, setDisplayData] = useState<Record<string, string>>(null);
  const [tabIndex, setTabIndex] = useState(0);
  const [EmailProviderList, setEmailProviderList] = useState<EmailProvider[]>([]);

  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

  //   const getData = () => { 
  //   }

  useEffect(() => {
    if (!props.item || props.item.id === 0) {
      setTabIndex(1);
    } else {
      setDisplayData({
        "ID": String(props.item.id),
        "Navn": props.item.name,
        "Brukernavn": props.item.username,
        "Passord": props.item.password,
        "E-postleverandør": props.item.sys_email_provider ? String(props.item.sys_email_provider) : "",
        "Godkjenning av bruker": props.item.auth_by_user ? "Ja" : "Nei",
      })
    }
  }, [props.item]);

  useEffect(() => {
    setEmailProviderList(props.EmailProviders)
  }, [props.EmailProviders]);

  const reset = () => {
    setItem(() => ({ ...item, password: "" })); // triggers props.EmailSetting change in EmailSettingForm
  }

  const saveItem = () => {
    if (!addEmailSettingRef.current) return;

    // Validate the form
    if (!addEmailSettingRef.current.valid()) {
      console.log("Validation failed");
      return;
    }

    // Get form data
    const EmailSettingRef = addEmailSettingRef.current.getItem();
    console.log("Saving data:", EmailSettingRef);

    if (addEmailSettingRef.current.valid()) {
      api("/manage/email/postEmailSetting", "POST", { ...EmailSettingRef }).then(res => {
        console.log("res:", res)
        if (res?.statusCode === 200) {
          if (res.email_setting) {
            onItemChange(res.email_setting);
          }
          setTabIndex(0);
        }
      });
    }
  };

  const changeTab = (index: number, performChange: boolean = true): boolean => {
    if (!item.id) {
      if (performChange) {
        setTabIndex(1);
      }

      return index === 1
    }

    if (performChange) {
      setTabIndex(index);
    }

    return true;
  }

  return (
    <>
      <AppBar position="static"
        sx={{
          backgroundColor: theme.palette.mode === 'dark' ? "inherit" : theme.palette.grey[300],
          color: theme.palette.mode === 'dark' ? "white" : "black"
        }}
      >
        <Tabs
          value={tabIndex}
          onChange={(_, newValue) => setTabIndex(newValue)}
          variant={"scrollable"}
          textColor="inherit"
          indicatorColor="primary"
          aria-label="manage EmailSetting tabs"
        >
          {[
            "Oversikt",
            item.id ? "Rediger" : "Opprett",
          ].map((name, index) => (
            <Tab disabled={!changeTab(index, false)} key={index} label={name} sx={{ fontWeight: "bold", textTransform: "none" }} {...TabProps(index)} />
          ))}
        </Tabs>
      </AppBar>
      <Box sx={{ mt: 2, p: { xs: 0, md: 3 }, minHeight: 400 }}>
        <TabPanel value={tabIndex} index={0} sx={{ minHeight: 200 }}>
          <Box sx={{ p: { xs: 1, md: 3 }, display: 'flex', justifyContent: 'center' }}>
            <Box sx={{ maxWidth: { xs: "100%", md: 1200 } }}>
              <DataInfoTable title={"EmailSetting"} data={displayData} onEdit={() => changeTab(1)} />
              {props.onClose && (
                <Box sx={{ width: '100%', display: 'flex', justifyContent: 'end', p: 2 }}>
                  <Button variant='contained' color='darkgrey' onClick={props.onClose}>Lukk</Button>
                </Box>
              )}
            </Box>
          </Box>
        </TabPanel>
        <TabPanel value={tabIndex} index={1} sx={{ minHeight: 200 }}>
          <Box sx={{ display: 'flex', flexDirection: "column", justifyContent: 'center', gap: 3, maxWidth: { xs: "100%", md: 1200 } }}>
            <EmailSettingForm item={item} EmailProviderList={EmailProviderList} error={getError} ref={addEmailSettingRef} />
            <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {/* <Button variant='contained' color='info' onClick={setDefault}>Default</Button> */}
                <Button variant='contained' color='darkgrey' onClick={reset}>Tilbakestill</Button>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                <Button
                  variant='contained'
                  color='darkgrey'
                  onClick={() => {
                    if (changeTab(0, false)) {
                      changeTab(0);
                    } else if (props.onClose) {
                      props.onClose();
                    }
                  }}
                >
                  Avbryt
                </Button>
                <Button
                  variant='contained'
                  color='info'
                  onClick={saveItem}
                  disabled={isLoading}
                  endIcon={isLoading && <CircularProgress size={22} />}
                >
                  {item.id ? "Lagre" : "Opprett"}
                </Button>
              </Box>
            </Box>
          </Box>
        </TabPanel>
        {/* <TabPanel value={tabIndex} index={2} sx={{ minHeight: 200 }}>
        <Box sx={{ p: { xs: 1, md: 3 }, display: 'flex', justifyContent: 'center' }}>
            <Box sx={{ maxWidth: 1200 }}>
              <EmailProviderForm 
                item={EmailProvider} 
                error={getError} 
                ref={addEmailProviderRef} 
              />
              <Box sx={{ width: "100%", display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 2, p: 2 }}>
                <Button variant="contained" color="darkgrey" onClick={() => setTabIndex(0)}>Avbryt</Button>
                <Button 
                  variant="contained" 
                  color="info" 
                  onClick={saveEmailProvider} 
                  disabled={isLoading} 
                  endIcon={isLoading && <CircularProgress size={22} />}
                >
                  {"Lagre"}
                </Button>
              </Box>
            </Box>
          </Box>
        </TabPanel> */}
      </Box>
    </>
  )
}